import { ChangeDetectionStrategy, Component, inject, input } from "@angular/core";
import { BaseComponent } from "primeng/basecomponent";
import { EmptystateStyle } from "./emptystate.style";

/**
 * Shows a visual hint when there's no data in a relevant component, and describes how to have it populated.
 * Despite the name, can sometimes be used as a dialog header.
 *
 * Additional content can be projected into the component.
 *
 * @example
 * ```html
 * <p-table>
 *   <ng-template #emptymessage>
 *     <tr>
 *       <td colspan="999">
 *         <ui-emptystate
 *           class="py-[3%]"
 *           image="/assets/layout/images/new-ui/book2.png"
 *           [caption]="t('emptystate.events.title')"
 *           [description]="t('emptystate.events.description')"
 *         >
 *           <a href="https://docs2.ankaadia.com/workflows/kandidatenmanagement/terminubersicht" target="_blank">
 *             {{ t("emptystate.events.more") }}
 *           </a>
 *         </ui-emptystate>
 *       </td>
 *     </tr>
 *   </ng-template>
 * </p-table>
 * ```
 *
 * @example
 * ```html
 * <p-drawer>
 *   <form class="dialog-form">
 *     <ui-emptystate
 *       image="/assets/layout/images/new-ui/laptop.png"
 *       [caption]="t('collection.new')"
 *       [description]="t('collection.newDescription')"
 *     />
 *   </form>
 * </p-drawer>
 * ```
 */
@Component({
  selector: "ui-emptystate",
  templateUrl: "./emptystate.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { "[class]": "cx('root')" },
})
export class EmptystateComponent extends BaseComponent {
  protected readonly _componentStyle = inject(EmptystateStyle);

  /**
   * Compact message stating the reason of the component to be rendered.
   */
  readonly caption = input.required<string>();

  /**
   * Detailed information about the situation and, perhaps, ways to have the relevant component populated with data.
   */
  readonly description = input.required<string>();

  /**
   * Path to the image that is displayed on top of the component.
   * Normally, starts with `/assets/layout/images/new-ui/`.
   */
  readonly image = input.required<string>();
}
